/* =========================================================
   HERE & ALWAYS — Wrapped PRODUCT-Level PDP V2.5

   Preserves V2.4 launcher styling and purchase split.
   Adds complimentary-notecard presentation for the exact
   Notecard variant, only when its displayed amount is zero.

   No input changes, synthetic clicks, cart requests,
   variant selection, or replacement of native buttons.
   ========================================================= */
(function () {
  'use strict';

  if (window.__haWrappedPdpSplitV25) return;
  window.__haWrappedPdpSplitV25 = true;

  const STYLE_ID = 'ha-wrapped-pdp-launcher-v24';
  const NOTE_STYLE_ID = 'ha-wrapped-pdp-note-v25';
  const NOTE_ATTR = 'data-ha-complimentary-note';
  const READY_CLASS = 'ha-pdp-purchase-split';
  const NOTE_VARIANT = '67977378005174';
  const controllers = new Map();

  const shadowCSS = `
    :host {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      height: 100% !important;
      min-height: 50px !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    gift-block-launcher {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    gift-block-launcher button.gift-block__launcher {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      min-height: 50px !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 4px !important;
      background: #fff !important;
      color: rgba(0,0,0,.55) !important;
      border: 1px solid rgba(0,0,0,.18) !important;
      border-right: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      font-family: "Bicyclette", Arial, sans-serif !important;
      font-size: 11px !important;
      font-weight: 400 !important;
      line-height: 1.25 !important;
      letter-spacing: .055em !important;
      text-transform: uppercase !important;
      text-align: center !important;
      white-space: nowrap !important;
      cursor: pointer !important;
      transition: border-color 160ms ease, color 160ms ease !important;
    }

    gift-block-launcher button.gift-block__launcher .button-content {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-direction: row !important;
      width: 100% !important;
      min-width: 0 !important;
      gap: 0 !important;
      white-space: nowrap !important;
    }

    gift-block-launcher button.gift-block__launcher .label {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      color: inherit !important;
      font-family: "Bicyclette", Arial, sans-serif !important;
      font-size: 11px !important;
      font-weight: 400 !important;
      line-height: 1.25 !important;
      letter-spacing: .055em !important;
      text-transform: uppercase !important;
      text-align: center !important;
      white-space: nowrap !important;
    }

    gift-block-launcher button.gift-block__launcher .icon-wrapper {
      display: none !important;
    }

    @media (hover: hover) {
      gift-block-launcher button.gift-block__launcher:hover {
        background: #fff !important;
        color: #111 !important;
        border-color: #111 !important;
        border-right: 0 !important;
        box-shadow: none !important;
      }
    }

    gift-block-launcher button.gift-block__launcher:focus-visible {
      outline: 1px solid #111 !important;
      outline-offset: 3px !important;
    }

    gift-block-launcher button.gift-block__launcher:disabled {
      opacity: .5 !important;
      cursor: not-allowed !important;
    }

    @media (max-width: 767px) {
      gift-block-launcher button.gift-block__launcher,
      gift-block-launcher button.gift-block__launcher .label {
        font-size: 10px !important;
        letter-spacing: .035em !important;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      gift-block-launcher button.gift-block__launcher {
        transition: none !important;
      }
    }
  `;

  /*
   * Hide the shared product title, price, and packaging policy.
   * Display our own heading without changing Shopify-bound content.
   */
  const noteCSS = `
    :host([${NOTE_ATTR}]) .gift-product-header,
    :host([${NOTE_ATTR}]) .gift-product-description {
      display: none !important;
    }

    .ha-wrapped-note-intro {
      margin: 0;
      color: inherit;
      font: inherit;
      visibility: hidden;
    }
  `;

  function ensureStyle(root, id, css) {
    if (root.getElementById(id)) return;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    root.appendChild(style);
  }

  function start(form) {
    if (controllers.has(form)) return;

    let frame = 0;
    let stopped = false;
    let watchedHost = null;
    let watchedRoot = null;
    let watchedActionsRoot = null;
    let rootObserver = null;
    let actionsObserver = null;
    let intro = null;

    const editedText = new Map();
    const timers = [];

    function schedule() {
      if (stopped || frame) return;

      frame = requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    }

    /*
     * Remember native labels so a change away from the
     * complimentary variant restores the app's presentation.
     */
    function setLabel(element, text) {
      if (!element) return;

      let record = editedText.get(element);

      if (!record) {
        record = {
          original: element.textContent,
          applied: null
        };
        editedText.set(element, record);
      } else if (element.textContent !== record.applied) {
        record.original = element.textContent;
      }

      if (element.textContent !== text) {
        element.textContent = text;
      }

      record.applied = text;
    }

    function resetNote() {
      watchedHost?.removeAttribute(NOTE_ATTR);
      intro?.remove();
      intro = null;

      editedText.forEach((record, element) => {
        if (element.textContent === record.applied) {
          element.textContent = record.original;
        }
      });

      editedText.clear();
    }

    const observation = {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'value',
        'amount',
        'variant-id',
        'hidden',
        'disabled',
        'inert',
        'open',
        'out-of-stock'
      ]
    };

    function watchRoots(host) {
      const root = host?.shadowRoot || null;

      if (host !== watchedHost || root !== watchedRoot) {
        rootObserver?.disconnect();
        actionsObserver?.disconnect();
        resetNote();

        watchedHost = host || null;
        watchedRoot = root;
        watchedActionsRoot = null;
        rootObserver = null;
        actionsObserver = null;

        if (root) {
          rootObserver = new MutationObserver(schedule);
          rootObserver.observe(root, observation);
        }
      }

      const actionsRoot =
        root?.querySelector('gift-block-actions')?.shadowRoot || null;

      if (actionsRoot !== watchedActionsRoot) {
        actionsObserver?.disconnect();
        watchedActionsRoot = actionsRoot;
        actionsObserver = null;

        if (actionsRoot) {
          actionsObserver = new MutationObserver(schedule);
          actionsObserver.observe(actionsRoot, observation);
        }
      }
    }

    function syncNote() {
      const root = watchedRoot;
      if (!root) return;

      const variantValue =
        root.querySelector('input[name="variantId"]')?.value || '';

      const variantId = variantValue.split('/').pop();

      const amount =
        root.querySelector('.gift-product-price')
          ?.getAttribute('amount');

      const isComplimentaryNote =
        variantId === NOTE_VARIANT &&
        amount !== null &&
        amount !== undefined &&
        amount.trim() !== '' &&
        Number.isFinite(Number(amount)) &&
        Number(amount) === 0;

      if (!isComplimentaryNote) {
        resetNote();
        return;
      }

      ensureStyle(root, NOTE_STYLE_ID, noteCSS);

      if (!watchedHost.hasAttribute(NOTE_ATTR)) {
        watchedHost.setAttribute(NOTE_ATTR, '');
      }

      // Discard references to nodes the app has replaced.
      editedText.forEach((record, element) => {
        if (!element.isConnected) editedText.delete(element);
      });

      root.querySelectorAll(
        'gift-block-launcher button.gift-block__launcher .label'
      ).forEach(label => {
        setLabel(label, 'WRITE A GIFT NOTE');
      });

      setLabel(
        root.querySelector('gift-dialog-header h1 span'),
        'A PERSONAL NOTE'
      );

      const product = root.querySelector(
        'dialog.gift-block-modal .gift-product'
      );

      if (product) {
        if (!intro || intro.parentElement !== product) {
          intro?.remove();
          intro = document.createElement('p');
          intro.className = 'ha-wrapped-note-intro';
          intro.textContent = 'Complimentary notecard';
          product.prepend(intro);
        }
      }

      /*
       * Only change the regular Save label.
       * Leave Unavailable, spinner, disabled and loading behavior native.
       */
      setLabel(
        watchedActionsRoot?.querySelector(
          '#save-button .label:not(.out-of-stock)'
        ),
        'SAVE NOTE'
      );
    }

    function sync() {
      if (!form.isConnected) {
        stop();
        return;
      }

      const children = Array.from(form.children);

      const gift = children.find(element => element.matches(
        'wrapped-gift-block[option-type="product"]' +
        '[target-group="product-page"]'
      ));

      const cart = children.find(element =>
        element.classList.contains('product-add-to-cart-container')
      );

      watchRoots(gift);

      const shadow = watchedRoot;

      const launcher = shadow && Array.from(
        shadow.querySelectorAll(
          'gift-block-launcher button.gift-block__launcher'
        )
      ).find(button =>
        !button.disabled &&
        !button.closest('[inert]') &&
        button.getClientRects().length > 0
      );

      const dialog = shadow?.querySelector('dialog.gift-block-modal');
      const giftIndex = children.indexOf(gift);
      const cartIndex = children.indexOf(cart);

      const between = giftIndex >= 0 && cartIndex > giftIndex
        ? children.slice(giftIndex + 1, cartIndex)
        : [];

      const unexpectedVisible = between.some(element => {
        const styles = getComputedStyle(element);

        return styles.display !== 'none' &&
          styles.visibility !== 'hidden' &&
          element.getBoundingClientRect().height > 1;
      });

      const giftVisible = !!gift &&
        getComputedStyle(gift).display !== 'none' &&
        getComputedStyle(gift).visibility !== 'hidden';

      const ready = !!(
        gift && cart &&
        giftIndex >= 0 && cartIndex > giftIndex &&
        giftVisible && launcher && dialog &&
        !unexpectedVisible &&
        cart.querySelector('button#AddToCart.single-add-to-cart-button')
      );

      if (ready) {
        ensureStyle(shadow, STYLE_ID, shadowCSS);
      }

      form.classList.toggle(READY_CLASS, ready);
      syncNote();
    }

    /*
     * A click can cause a nested shadow root to hydrate.
     * These bounded checks also catch its subsequent render.
     */
    function afterInteraction() {
      schedule();
      [80, 250, 600].forEach(delay => {
        const timer = setTimeout(() => {
          const index = timers.indexOf(timer);
          if (index >= 0) timers.splice(index, 1);
          schedule();
        }, delay);
        timers.push(timer);
      });
    }

    const formObserver = new MutationObserver(schedule);
    formObserver.observe(form, { childList: true });
    form.addEventListener('click', afterInteraction, true);
    form.addEventListener('change', afterInteraction, true);

    function stop() {
      if (stopped) return;
      stopped = true;

      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
      formObserver.disconnect();
      rootObserver?.disconnect();
      actionsObserver?.disconnect();

      form.removeEventListener('click', afterInteraction, true);
      form.removeEventListener('change', afterInteraction, true);

      resetNote();
      form.classList.remove(READY_CLASS);
      controllers.delete(form);
    }

    controllers.set(form, { stop });
    sync();

    [100, 350, 900, 1800].forEach(delay => {
      timers.push(setTimeout(schedule, delay));
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('.ha-pdp-hero-v2 form').forEach(form => {
      if (form.querySelector('.product-add-to-cart-container')) {
        start(form);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, form) => {
      if (event.target.contains(form)) controller.stop();
    });
  });
})();